import type { GraphParams } from './meta/graph';
import type { LeadFormQuestion, MetaLeadFormSpec } from './types';

// Growth's standard Instant Form. Duplicating another form replaces all of it; every field stays editable.

/** The standard questions, in the order Meta offers them, with how they read in the form. */
export const LEAD_FORM_QUESTION_LABELS: Record<string, string> = {
  WORK_EMAIL: 'Work email',
  EMAIL: 'Email',
  FULL_NAME: 'Full name',
  FIRST_NAME: 'First name',
  LAST_NAME: 'Last name',
  COMPANY_NAME: 'Company name',
  JOB_TITLE: 'Job title',
  PHONE: 'Phone number',
  WORK_PHONE_NUMBER: 'Work phone number',
  WEBSITE: 'Website',
  CITY: 'City',
  STATE: 'State',
  COUNTRY: 'Country',
  ZIP: 'Zip code',
};

export const questionLabel = (q: LeadFormQuestion) => q.label || LEAD_FORM_QUESTION_LABELS[q.type] || q.type;

export const GROWTH_FORM_QUESTIONS: LeadFormQuestion[] = [
  { type: 'WORK_EMAIL' },
  { type: 'FULL_NAME' },
  { type: 'COMPANY_NAME' },
  { type: 'JOB_TITLE' },
  { type: 'PHONE' },
];

export const formName = (program: string) => `${program.trim() || 'Untitled'} Livestream Form (FB Ads)`;

export function growthFormDefaults(program: string): MetaLeadFormSpec {
  return {
    name: formName(program),
    optimizedForQuality: false,
    questionHeadline: '',
    questions: GROWTH_FORM_QUESTIONS,
    privacyPolicy: { url: 'https://privacy.clay.com/policies', linkText: "Visit Clay GTM's Privacy Policy." },
    thankYou: {
      title: 'Thanks for registering!',
      body: "We'll send you an email with the calendar invite.  Can't wait to see you there.",
      buttonType: 'VIEW_WEBSITE',
      buttonText: 'Learn more',
      websiteUrl: 'https://www.clay.com/',
    },
    contextCard: null,
    locale: 'EN_US',
  };
}

/** The `POST /{page_id}/leadgen_forms` params for a spec. Without a greeting, `context_card` is left out. */
export function toCreateParams(spec: MetaLeadFormSpec): GraphParams {
  return {
    name: spec.name,
    locale: spec.locale,
    is_optimized_for_quality: spec.optimizedForQuality,
    question_page_custom_headline: spec.questionHeadline || undefined,
    questions: spec.questions.map(q => ({
      type: q.type,
      ...(q.key && { key: q.key }),
      ...(q.label && { label: q.label }),
      ...(q.inlineContext && { inline_context: q.inlineContext }),
      ...(q.options?.length && { options: q.options }),
    })),
    privacy_policy: { url: spec.privacyPolicy.url, link_text: spec.privacyPolicy.linkText },
    thank_you_page: {
      title: spec.thankYou.title,
      body: spec.thankYou.body,
      button_type: spec.thankYou.buttonType,
      ...(spec.thankYou.buttonText && { button_text: spec.thankYou.buttonText }),
      ...(spec.thankYou.websiteUrl && { website_url: spec.thankYou.websiteUrl }),
    },
    context_card: spec.contextCard
      ? {
          title: spec.contextCard.title,
          style: spec.contextCard.style,
          content: spec.contextCard.content,
          button_text: spec.contextCard.buttonText,
        }
      : undefined,
  };
}
